/**
 * Hook para buscar e gerenciar dados de processos
 * Equivalente ao useUnitsData, mas para a view de Processos
 */
import { useQuery } from '@tanstack/react-query';
import { useProcessosFilters } from './useProcessosFilters';
import { API_BASE_URL } from '@/lib/api';

const API_BASE = `${API_BASE_URL}/claro/api/processes`;

// Tipos para respostas da API

/** KPIs de processos */
export interface ProcessesKpisResponse {
  total: number;
  andamento: number;
  atrasado: number;
}

/** Status count para gráficos */
export interface StatusCount {
  status: string;
  count: number;
}

/** Linha de tipo de documento */
export interface TypeStatusRow {
  docType: string;
  total: number;
  statuses: StatusCount[];
}

/** Resposta do endpoint by-type-status */
export interface TypeStatusResponse {
  rows: TypeStatusRow[];
  statuses: string[];
}

/** Contagem de equipe por fornecedor */
export interface TeamCount {
  team: string;
  count: number;
}

/** Linha de fornecedor */
export interface SupplierRow {
  company: string;
  totalProcesses: number;
  distinctTeams: number;
  teamCounts: TeamCount[];
  examples: string[];
}

/** Resposta do endpoint by-supplier */
export interface SuppliersResponse {
  rows: SupplierRow[];
  grandTotal: number;
}

/**
 * Helper para fazer fetch da API
 */
async function fetchJson<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
  }
  
  const response = await fetch(url.toString());
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  
  const json = await response.json();
  // A API retorna { status, result, ... } - extrair result se existir
  return json.result ?? json;
}

/**
 * Hook para buscar KPIs de processos (total, em andamento, atrasado)
 */
export function useProcessesKpis() {
  // Selecionar estado diretamente para reatividade
  const docTypes = useProcessosFilters((state) => state.docTypes);
  const regional = useProcessosFilters((state) => state.regional);
  const uf = useProcessosFilters((state) => state.uf);
  
  // Construir params
  const params: Record<string, string> = {};
  if (regional) params.regional = regional;
  if (uf) params.uf = uf;
  if (docTypes && docTypes.length > 0) params.docTypes = docTypes.join(',');

  return useQuery({
    queryKey: ['processes', 'kpis', params],
    queryFn: () => fetchJson<ProcessesKpisResponse>('/kpis', params),
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 30, // 30 minutos
  });
}

/**
 * Hook para buscar matriz Tipo x Status (para gráfico stacked bar)
 */
export function useProcessesByTypeStatus() {
  // Selecionar estado diretamente para reatividade
  const docTypes = useProcessosFilters((state) => state.docTypes);
  const regional = useProcessosFilters((state) => state.regional);
  const uf = useProcessosFilters((state) => state.uf);
  
  // Construir params
  const params: Record<string, string> = {};
  if (regional) params.regional = regional;
  if (uf) params.uf = uf;
  if (docTypes && docTypes.length > 0) params.docTypes = docTypes.join(',');

  return useQuery({
    queryKey: ['processes', 'by-type-status', params],
    queryFn: () => fetchJson<TypeStatusResponse>('/by-type-status', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar processos por fornecedor (para gráfico stacked bar)
 */
export function useProcessesBySupplier() {
  // Selecionar estado diretamente para reatividade
  const docTypes = useProcessosFilters((state) => state.docTypes);
  const regional = useProcessosFilters((state) => state.regional);
  const uf = useProcessosFilters((state) => state.uf);
  
  // Construir params
  const params: Record<string, string> = {};
  if (regional) params.regional = regional;
  if (uf) params.uf = uf;
  if (docTypes && docTypes.length > 0) params.docTypes = docTypes.join(',');

  return useQuery({
    queryKey: ['processes', 'by-supplier', params],
    queryFn: () => fetchJson<SuppliersResponse>('/by-supplier', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/** Contagem por fornecedor */
export interface SupplierCount {
  supplier: string;
  count: number;
}

/** Linha de pendência por tipo */
export interface PendencyRow {
  pendencyType: string;
  total: number;
  bySupplier: SupplierCount[];
}

/** Resposta do endpoint by-pendency */
export interface PendenciesResponse {
  rows: PendencyRow[];
  suppliers: string[];
  grandTotal: number;
  availableFilters: {
    suppliers: string[];
    pendencyTypes: string[];
  };
}

/**
 * Hook para buscar pendências por tipo x fornecedor (para gráfico stacked bar)
 */
export function useProcessesByPendency() {
  // Selecionar estado diretamente para reatividade
  const docTypes = useProcessosFilters((state) => state.docTypes);
  const regional = useProcessosFilters((state) => state.regional);
  const uf = useProcessosFilters((state) => state.uf);
  const pendencyTypes = useProcessosFilters((state) => state.pendencyTypes);
  const suppliers = useProcessosFilters((state) => state.suppliers);
  
  // Construir params
  const params: Record<string, string> = {};
  if (regional) params.regional = regional;
  if (uf) params.uf = uf;
  if (docTypes && docTypes.length > 0) params.docTypes = docTypes.join(',');
  if (pendencyTypes && pendencyTypes.length > 0) params.pendencyTypes = pendencyTypes.join(',');
  if (suppliers && suppliers.length > 0) params.suppliers = suppliers.join(',');

  return useQuery({
    queryKey: ['processes', 'by-pendency', params],
    queryFn: () => fetchJson<PendenciesResponse>('/by-pendency', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}
